import type { PaginationMetaDto } from './generated/Api';

/**
 * The `meta` block of every paginated list. It is the backend's generated
 * `PaginationMetaDto`, not a copy of it, so a key the backend renames is a
 * type error here rather than an `undefined` on a screen.
 */
export type PaginationMeta = PaginationMetaDto;

/**
 * Every paginated list the API returns: `{ data: Item[], meta }`.
 *
 * Type every list request with this. Before it, each hook declared its own
 * `{ data, total, page }` shape, and some had drifted from what the backend
 * sent: the customer list's type promised a `meta.total` that never existed,
 * and the driver app's trucks read `data` from an endpoint that returned
 * `trucks`.
 */
export interface Paginated<Item> {
  data: Item[];
  meta: PaginationMeta;
}

export type SortOrder = 'asc' | 'desc';

/**
 * Paging and ordering for a list request: the frontend half of the backend's
 * `ListQueryDto`.
 *
 * `SortBy` is the endpoint's generated enum (`CallSortBy`, `TruckSortBy`),
 * built from the same allow-list the endpoint sorts with, so a request cannot
 * name a field the endpoint would ignore.
 */
export interface ListParams<SortBy extends string = never> {
  pageNo?: number;
  pageSize?: number;
  sortBy?: SortBy;
  sortOrder?: SortOrder;
}

/** How many pages `meta` describes. The backend does not send this. */
export function pageCount(meta: PaginationMeta): number {
  return meta.pageSize > 0 ? Math.ceil(meta.totalCount / meta.pageSize) : 0;
}

/**
 * A request's query string, `?`-prefixed, or empty when nothing is set.
 *
 * `undefined` and `null` are left out rather than sent as the strings
 * "undefined" and "null", and so is an empty string, since a cleared search
 * box means no filter. An array is sent comma-separated, which is how the
 * list hooks have always sent `status` and `drivers`, and what `GET /calls`
 * splits on.
 *
 * Takes any object so a hook's params interface can be passed as it is.
 */
export function toQueryString(params: object = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}
